<template>
  <div
    :class="{
      'issue-field': true,
      'issue-field--editing': editing,
      'issue-field--error': hasError,
    }"
  >
    <div class="issue-field__wrapper">
      <div
        v-if="!editing"
        class="issue-field__menu"
      >
        <div class="dropdown">
          <div class="dropdown__trigger hide-for-medium">
            <UseSvg
              class="icon"
              name="vertical_ellipsis"
              @click="setShowPopper"
            />
          </div>
          <Popper
            v-if="showPopper"
            v-model:open="open"
            placement="bottom-end"
            @click="setShowPopper"
          >
            <template #trigger>
              <div class="dropdown__trigger show-for-medium">
                <UseSvg
                  class="icon"
                  name="vertical_ellipsis"
                />
              </div>
            </template>

            <div class="menu menu--dropdown">
              <ul class="menu__items">
                <li class="menu-item menu-item--dropdown typo-list-item">
                  <button
                    class="menu-item__link"
                    type="button"
                    @click="beginEdit"
                  >
                    <span class="menu-item__label">
                      {{ editFieldName }}
                    </span>
                  </button>
                </li>
              </ul>
            </div>
          </Popper>
        </div>
      </div>

      <div
        :class="{
          'issue-field__body issue-field__title--small-text': true,
          'issue-field__body--padded-right': !editing,
        }"
      >
        <textarea
          id="description"
          ref="input"
          v-model="currentValue"
          v-autoresize
          class="issue__input typo-ignore"
          :disabled="!editing"
          :placeholder="$t('general.image_description_placeholder')"
          rows="4"
        />
      </div>
    </div>

    <!-- Actions -->
    <div
      v-if="editing && currentImage"
      class="issue-field__actions issue-field__actions--padded-top"
    >
      <div
        v-if="errors"
        class="issue-field__errors"
      >
        <FormRow
          :errors="errors"
          :modifiers="['unpadded']"
        />
      </div>

      <button
        class="button button--small-flat"
        type="button"
        @click="cancelEdit"
      >
        {{ $t('general.cancel') }}
      </button>
      <button
        class="button button--inverse button--small-flat"
        type="button"
        @click="updateField"
      >
        {{ $t('general.save') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

import useAjax from '@/functions/use_ajax';

import vAutoresize from '@/directives/autoresize';

import FormRow from '@/components/form/row.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Popper from '@/components/generic/popper.vue';

const props = defineProps({
  /** The currently selected image object */
  currentImage: { type: Object, required: true },
  /** The currently selected issue internal path */
  issuePath: { type: String, required: true },
  /** A list of all issue images */
  images: { type: Array, default: () => [] },
});

const emit = defineEmits(['updateImageDescription']);

const ajax = useAjax();

const open = ref(false);
const editing = ref(false);
const input = ref(null);
const errors = ref(null);
const showPop = ref(false);
const mobileView = ref(smallWindow());
const currentValue = ref(props.currentImage?.description);
const route = useRoute();

const showPopper = computed(() => {
  return showPop.value || mobileView.value === false;
});

const hasError = computed(() => {
  return !!errors.value;
});

const isShaft = computed(() => {
  return !route.path.includes('shafts_v2') && route.params.shaftId;
});

const selectedImage = computed(() => {
  if (!props.currentImage) return;

  return props.images.find((image) => {
    return image['image_blob']['id'] === props.currentImage['image_blob']['id'];
  });
});

const editFieldName = computed(() => {
  return I18n.t('general.edit_field', {
    field_name: I18n.t('issues.description').toLowerCase(),
  });
});

/**
 * The second popper instance on top of the sidebar in mobile causes issues,
 * so should be manually displayed or hidden (otherwise it always stays on top)
 */
onMounted(() => {
  window.addEventListener('resize', setMobileView);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setMobileView);
});

watch(
  () => props.currentImage,
  () => {
    editing.value = false;
    currentValue.value = props.currentImage?.description;
  }
);

function setShowPopper() {
  showPop.value = mobileView.value ? !showPop.value : false;
}

function setMobileView() {
  mobileView.value = smallWindow();
}

function smallWindow() {
  return window.innerWidth < 768;
}

async function beginEdit() {
  editing.value = true;
  open.value = false;

  await nextTick();
  input.value.focus();
  input.value.select();
}

function updateBody() {
  if (isShaft.value) {
    return {
      activity: {
        detail_attributes: {
          issue_images_attributes: [
            {
              id: selectedImage.value['id'],
              description: currentValue.value
            },
          ],
        },
      },
    };
  } else {
    return {
      issue: {
        id: props.currentImage.issue_id,
        issue_images_attributes: [
          {
            id: selectedImage.value['id'],
            description: currentValue.value,
          },
        ],
      },
    };
  }
}

/**
 * For new issues, emit the image to be saved along with the main issue
 * For existing issues, just persist to API straight away
 */
async function updateField() {
  errors.value = null;

  emit('updateImageDescription', currentValue.value);

  if (!props.currentImage?.issue_id && !selectedImage.value['issue_id']) {
    editing.value = false;
    return;
  }

  let { ok, data } = await ajax(props.issuePath, {
    method: 'PATCH',
    body: updateBody(),
  });

  if (ok) {
    editing.value = false;
  } else {
    if (isShaft.value) {
      errors.value = data?.activity['detail.issue_images.description'] || [
        I18n.t('general.server_error.title'),
      ];
    } else {
      errors.value = data?.issue?.issue_images || [
        I18n.t('general.server_error.title'),
      ];
    }
  }
}

function cancelEdit() {
  editing.value = false;
  errors.value = null;
  currentValue.value = props.currentImage?.description;
}
</script>
